import { Button } from '@flowus/common/components/button';
import { message } from '@flowus/common/components/message';
import { cx } from '@flowus/common/cx';
import { useApiState } from '@flowus/common/hooks/use-api-state';
import { useOpenModal } from '@flowus/common/next-modal';
import { Tooltip } from '@flowus/common/tooltip';
import { getCurrencySymbols } from '@flowus/common/utils';
import { Modals, SpacePlanType } from '@flowus/shared';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { Icon } from 'src/common/components/icon';
import { PersonSpaceFreeCapacity, PersonSpaceFreeCopyLimit, publicLink } from 'src/common/const';
import { createModel, useModel } from 'src/common/create-model';
import { request } from 'src/common/request';
import { getReadableFileSizeString } from 'src/editor/utils/size-utils';
import { useCurrentSpace, useSpace } from 'src/hooks/space';
import { OpenDialogEnum } from 'src/hooks/space/use-open-dialog-from-location';
import { useLimitConfig } from 'src/services/app/hook/subscription-data';
import { useSpaceCapacity } from 'src/services/capacity';
import { SearchParams } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { getSpacePlanTypeName } from 'src/utils/block-utils';
import { OpenSettingFrom } from 'src/views/main/setting-modal/type';
import { useSpacePlanDataHeader } from 'src/views/main/setting-modal/upgrade-setting/upgrade-setting-content';
import { useOpenUpgradeSpace } from 'src/views/main/setting-modal/use-open-setting-modal';
import { HelperIcon } from '../helper-icon';
import { useOpenUpgradeCapacityModal } from '../select-upgrade-plan/upgrade-capacity';
import { UpgradeSpacePlanType } from './types';

interface UpgradeSpacePlanProps {
  /**
   * @maxCapacity 达到空间上限
   * @payCapacity 购买容量包
   * @maxGuestCount 升级购买协作者数量
   * @maxFileSize 突破文件限制
   */
  type: UpgradeSpacePlanType;
  spaceId?: string;
}

const useUpgradeSpacePlan = (props: UpgradeSpacePlanProps) => {
  return { ...props };
};

const UpgradeSpacePlanContext = createModel(useUpgradeSpacePlan);
const useUpgradeSpacePlanContext = () => useModel(UpgradeSpacePlanContext);

const UpgradeSpacePlan: FC<{ spaceId?: string }> = (props) => {
  const { spaceId } = props;
  const { t } = useAppTranslation();
  const currentSpace = useCurrentSpace();
  const space = useSpace(spaceId);
  const isOtherSpace = spaceId !== currentSpace.uuid;
  const openModal = useOpenModal();
  const { type } = useUpgradeSpacePlanContext();
  const { guestMaxLimitDesc } = useLimitConfig();
  const spacePlanName = getSpacePlanTypeName(space.uuid ? space.planType : currentSpace.planType);
  const spaceCapacity = useSpaceCapacity(spaceId);

  const handleClose = useCallback(() => {
    openModal.closeModal(Modals.MINI_PRICE_PLAN_MODAL);
  }, [openModal]);

  const [header, content] = useMemo(() => {
    const _content = {
      title: null as ReactNode,
      desc: null as ReactNode,
      popup: null as ReactNode,
      content: null as ReactNode,
    };
    if (type === UpgradeSpacePlanType.maxCapacity) {
      _content.title = [space.uuid ? `「${space.title}」` : '', t('common.capacity_limit')].join(
        ''
      );
      _content.desc = (
        <>
          {spacePlanName}
          {t('components.upgrade_plan.max_capacity_desc_part1')}
          {getReadableFileSizeString(spaceCapacity.maxCapacity)}
          {t('components.upgrade_plan.max_capacity_desc_part2')}
        </>
      );
      _content.popup = t('components.upgrade_plan.max_capacity_popup', {
        spacePlanName,
        capacity: getReadableFileSizeString(spaceCapacity.maxCapacity),
      });
      _content.content = (
        <>
          <Panel.PersonPanel from={OpenSettingFrom.dialog_capacity_limit} />
          <Panel.ProPersonPanel
            from={OpenSettingFrom.dialog_capacity_limit}
            jumpSpaceId={isOtherSpace ? space.uuid : undefined}
          />
        </>
      );
    }

    // if (type === UpgradeSpacePlanType.payCapacity) {
    //   _content.title = <>还不想升级？试试购买容量包</>;
    //   _content.content = (
    //     <>
    //       <Panel.SpaceCapacityPanel />
    //       <Panel.ProPersonPanel />
    //     </>
    //   );
    // }

    if (type === UpgradeSpacePlanType.maxCopyLimit) {
      _content.title = <>{t('components.upgrade_plan.max_copy_limit_title')}</>;
      _content.desc = (
        <>
          {t('components.upgrade_plan.max_copy_limit_desc_part1')}
          {spacePlanName}
          {t('components.upgrade_plan.max_copy_limit_desc_part2')}
          {PersonSpaceFreeCopyLimit}
          {t('components.upgrade_plan.max_copy_limit_desc_part3')}
        </>
      );
      _content.content = (
        <>
          <Panel.PersonPanel from={OpenSettingFrom.dialog_copy_limit} />
          <Panel.ProPersonPanel
            from={OpenSettingFrom.dialog_copy_limit}
            jumpSpaceId={isOtherSpace ? space.uuid : undefined}
          />
        </>
      );
    }

    if (type === UpgradeSpacePlanType.maxFileSize) {
      _content.title = <>{t('common.file_size_limit')}</>;
      _content.desc = (
        <>
          {spacePlanName}
          {t('components.upgrade_plan.max_file_size_desc')}
        </>
      );
      _content.content = (
        <>
          <Panel.PersonPanel from={OpenSettingFrom.file} />
          <Panel.ProPersonPanel
            from={OpenSettingFrom.file}
            jumpSpaceId={isOtherSpace ? space.uuid : undefined}
          />
        </>
      );
    }
    if (type === UpgradeSpacePlanType.maxGuestCount) {
      _content.title = <>{t('components.upgrade_plan.max_guest_count_title')}</>;
      _content.desc = (
        <>
          {spacePlanName}
          {t('components.upgrade_plan.max_guest_count_desc_part1')}
          {guestMaxLimitDesc}
          {t('components.upgrade_plan.max_guest_count_desc_part2')}
        </>
      );
      _content.popup = t('components.upgrade_plan.max_guest_count_popup', {
        spacePlanName,
        guestMaxLimitDesc,
      });
      _content.content = (
        <>
          <Panel.PersonPanel from={OpenSettingFrom.guest_max} />
          <Panel.ProPersonPanel
            from={OpenSettingFrom.guest_max}
            jumpSpaceId={isOtherSpace ? space.uuid : undefined}
          />
        </>
      );
    }
    if (type === UpgradeSpacePlanType.subscribeAiLimit) {
      _content.title = (
        <div className="flex items-center">
          <div>{t('components.upgrade_plan.subscribe_ai_limit_title')}</div>
          <Tooltip popup={t('components.upgrade_plan.subscribe_ai_limit_popup')}>
            <Icon name={'IcInviteHelp'} size="middle" />
          </Tooltip>
        </div>
      );
      _content.desc = <>{t('components.upgrade_plan.subscribe_ai_limit_desc')}</>;
      _content.content = (
        <>
          <Panel.PersonPanel from={OpenSettingFrom.dialog_subscribe_ai} />
          <Panel.ProPersonPanel
            from={OpenSettingFrom.dialog_subscribe_ai}
            jumpSpaceId={isOtherSpace ? space.uuid : undefined}
          />
        </>
      );
    }

    const _header = (
      <>
        <div className="w-full text-h2 pl-7 pr-4 py-7 flex items-center justify-between">
          <div>
            <span>{_content.title}</span>
            {_content.popup && <HelperIcon popup={_content.popup} size="middle" />}
          </div>
          <Icon className="text-grey3" size="middle" name="IcClose" onClick={handleClose} />
        </div>
        <div className="w-full px-7 py-2.5 text-t3 text-grey3">{_content.desc}</div>
      </>
    );

    return [_header, _content.content];
  }, [
    t,
    guestMaxLimitDesc,
    handleClose,
    isOtherSpace,
    space.title,
    space.uuid,
    spaceCapacity.maxCapacity,
    spacePlanName,
    type,
  ]);

  useEffect(() => {
    bizTracker.event(type);
  }, [type]);

  return (
    <div>
      {header}
      <div className="grid grid-cols-2 gap-5 px-7 pb-6">{content}</div>
    </div>
  );
};

// #region Panel

const getFuncListData = (t: any) =>
  ({
    freePersonCapacity: [
      {
        type: 'title',
        value: t('components.upgrade_plan.features_title'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.free_storage', { capacity: PersonSpaceFreeCapacity }),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.free_upload_limit'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.free_guest_count'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.free_copy_limit', { limit: PersonSpaceFreeCopyLimit }),
      },
    ],
    proPerson: [
      {
        type: 'title',
        value: t('components.upgrade_plan.pro_includes_title'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.pro_storage'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.pro_upload_limit'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.pro_guest_count'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.pro_copy_limit'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.pro_history_retention'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.pro_templates'),
      },
      {
        type: 'select',
        value: t('components.upgrade_plan.pro_mindmap_nodes'),
      },
      {
        type: 'link',
        value: t('common.learn_more'),
        link: publicLink.pricing,
      },
    ],
    capacity: [],
  } as const);

type FuncListDataKey = 'freePersonCapacity' | 'proPerson' | 'capacity';

const FunctionList: FC<{ type: FuncListDataKey }> = (props) => {
  const { type } = props;
  const { t } = useAppTranslation();
  const funcListData = getFuncListData(t);

  return (
    <>
      {funcListData[type].map((item) => (
        <div
          key={item.value}
          className={cx(
            'flex items-center space-x-2 py-2 text-t2',
            item.type === 'title' && 'text-grey3'
          )}
        >
          {item.type === 'select' && (
            <>
              <Icon size="small" className="text-active_color" name="IcCheck01" />
              <span>{item.value}</span>
            </>
          )}
          {item.type === 'link' && (
            <a href={item.link} target="_blank" className="text-active_color text-t4">
              {item.value}
            </a>
          )}
        </div>
      ))}
    </>
  );
};

const PanelContainer: FC<{
  title?: ReactNode;
  type: FuncListDataKey;
  price?: ReactNode;
  onClick: () => void;
}> = (props) => {
  const { t } = useAppTranslation();
  const { title, type, price, children, onClick } = props;
  const isPro = ['proPerson'].includes(type);

  return (
    <div className={'border rounded flex flex-col'}>
      {title && (
        <div
          className={cx('text-h4 p-3 bg-grey9', isPro && 'bg-active_color_10 text-active_color')}
        >
          {title}
        </div>
      )}
      <div className="py-4 px-3 flex flex-col flex-1">
        <div>{price}</div>
        <FunctionList type={type} />
        {children}
        {isPro && (
          <Button className="self-end w-full" colorType="active" size="large" onClick={onClick}>
            {t('common.upgrade')}
          </Button>
        )}
      </div>
    </div>
  );
};

const SpaceCapacityPanel: FC = () => {
  const { t } = useAppTranslation();
  const currentSpace = useCurrentSpace();
  const [planList = { list: [] }] = useApiState({
    getApi: () => request.infra.capacityPlanList(currentSpace.uuid),
    enable: !!currentSpace.uuid,
  });
  const openUpgradeSpace = useOpenUpgradeSpace();
  const openUpgradeCapacityModal = useOpenUpgradeCapacityModal();

  return (
    <PanelContainer
      title={t('components.upgrade_plan.capacity_package')}
      type="capacity"
      onClick={() => {
        openUpgradeSpace(OpenSettingFrom.dialog_capacity_package);
      }}
    >
      {planList.list?.map((item) => (
        <div className="text-h3 py-2 flex items-center justify-between">
          <span>{item.name}</span>
          <span>
            <span>
              {getCurrencySymbols(item.currentPriceUnit)}
              {item.currentPrice}
            </span>
            <span className="text-t1 text-grey3">{t('components.upgrade_plan.per_year')}</span>
          </span>
        </div>
      ))}
      <div className="self-end w-full mt-auto">
        <Button
          colorType="active_line"
          size="auto"
          onClick={() => {
            openUpgradeCapacityModal({ isFrom: OpenSettingFrom.dialog_capacity_package });
          }}
        >
          {t('common.buy_capacity_package')}
        </Button>
      </div>
    </PanelContainer>
  );
};

const PersonPanel: FC<{ from: OpenSettingFrom }> = ({ from }) => {
  const { t } = useAppTranslation();
  const currentSpace = useCurrentSpace();
  const openUpgradeSpace = useOpenUpgradeSpace();
  return (
    <PanelContainer
      onClick={() => {
        openUpgradeSpace(from);
      }}
      price={<div className="text-h2">{t('common.free_plan')}</div>}
      title={
        <div className="space-x-1">
          <span>{t('components.upgrade_plan.free_person_space')}</span>
          {currentSpace.planType === SpacePlanType.freePersonal && (
            <span className="text-t4 text-grey3 bg-black_006 rounded px-1 py-0.5">
              {t('components.upgrade_plan.current_plan')}
            </span>
          )}
        </div>
      }
      type="freePersonCapacity"
    />
  );
};

const ProPersonPanel: FC<{
  from: OpenSettingFrom;
  jumpSpaceId?: string;
}> = ({ from, jumpSpaceId }) => {
  const { t } = useAppTranslation();
  const { planData } = useSpacePlanDataHeader({
    isFrom: OpenSettingFrom.file,
  });
  const { type: modalType } = useUpgradeSpacePlanContext();
  const isPayCapacity = modalType === UpgradeSpacePlanType.payCapacity;
  const openUpgradeSpace = useOpenUpgradeSpace();

  const price = useMemo(() => {
    const curPrice = (
      <span>
        <span className="text-h2 text-black">
          {getCurrencySymbols(planData.proPerson.currentPriceUnit)}
          {planData.proPerson.currentPrice}
        </span>
        <span>{t('components.upgrade_plan.per_year')}</span>
      </span>
    );

    return (
      <div className="text-grey3 space-x-1">
        {isPayCapacity && (
          <div className="space-x-1">
            <span className="text-black text-h2">200G</span>
            <span>{t('common.storage_capacity')}</span>
          </div>
        )}
        {curPrice}
        <span className="line-through">
          {getCurrencySymbols(planData.proPerson.originalPriceUnit)}
          {planData.proPerson.originalPrice}
          {t('components.upgrade_plan.per_year')}
        </span>
        {planData.proPerson.label && (
          <span className="bg-red text-white text-t4 rounded px-1.5 py-0.5">
            {planData.proPerson.label}
          </span>
        )}
      </div>
    );
  }, [
    isPayCapacity,
    planData.proPerson.currentPrice,
    planData.proPerson.currentPriceUnit,
    planData.proPerson.label,
    planData.proPerson.originalPrice,
    planData.proPerson.originalPriceUnit,
    t,
  ]);

  return (
    <PanelContainer
      onClick={() => {
        if (jumpSpaceId) {
          // 跳到对应的空间再打开升级
          message.success(t('components.upgrade_plan.switching_space') as string);
          // 切换空间再升级
          const params = new URLSearchParams();
          params.set(SearchParams.spaceId, jumpSpaceId);
          params.set(SearchParams.openDialog, OpenDialogEnum.openUpgradeSpace);
          window.location.href = `${window.location.origin}?${params.toString()}`;
        } else {
          openUpgradeSpace(from);
        }
      }}
      price={price}
      title={
        <div className="flex items-center space-x-1">
          <span>{t('common.pro_version')}</span>
          {isPayCapacity && (
            <span className="bg-black-base text-white rounded px-1.5 py-0.5 text-t4">
              {t('components.upgrade_plan.most_popular')}
            </span>
          )}
        </div>
      }
      type="proPerson"
    />
  );
};
// #endregion

const Panel = {
  PersonPanel,
  ProPersonPanel,
  SpaceCapacityPanel,
};

export const useOpenUpgradeSpacePlan = () => {
  // const openModal = useOpenModal();
  const openUpgradeSpace = useOpenUpgradeSpace();

  return useCallback(
    (params: UpgradeSpacePlanProps) => {
      openUpgradeSpace(OpenSettingFrom.members);
      // if (__BUILD_IN__) {
      //   return;
      // }
      // openModal.modal({
      //   modalId: Modals.MINI_PRICE_PLAN_MODAL,
      //   content: () => (
      //     <UpgradeSpacePlanContext {...params}>
      //       <UpgradeSpacePlan spaceId={params.spaceId} />
      //     </UpgradeSpacePlanContext>
      //   ),
      // });
    },
    [openUpgradeSpace]
  );
};
